// Evidence table — "the evidence table of closest document pairs (each row
// governed by per-document grant access — a deny is never upgraded, and a
// row the viewer cannot read shows no title; excluded shared attachments
// visibly labelled under the same governance)" (design §6.3). Every visible
// similarity here uses the THIRD anchor phrasing (design §5.1): "How close
// these two documents are: N".
//
// No backend route serves this shape yet (see types/whitespace.ts's DATA
// GAPS note on OverlapGroup) — GroupDetail.tsx always calls this with
// `rows={[]}` today. The row shape below is this component's own forward-
// looking contract, not a verified backend type, so it lives here rather
// than in types/whitespace.ts (which only models real, verified routes).
import { overlapDocumentPairAnchor } from '../../lib/whitespace'

export interface EvidenceTableRow {
  document_a_id: string
  document_a_title: string | null
  document_a_type: string | null
  document_b_id: string
  document_b_title: string | null
  document_b_type: string | null
  /** 0-100 floor-integer, or `null` for an excluded shared-attachment row (which carries `excluded_reason` instead). */
  cosine_pct: number | null
  excluded_reason: string | null
}

interface EvidenceTableProps {
  rows: EvidenceTableRow[]
  rowCount?: number
  shownCount?: number
}

function DocCell({ title, type }: { title: string | null; type: string | null }) {
  if (title === null) {
    return <span className="italic text-gray-400">Document — not visible at your access level</span>
  }
  return (
    <span>
      {type && (
        <span className="inline-block text-[9px] font-extrabold uppercase tracking-wide rounded px-1 py-0.5 mr-1 bg-violet-100 text-violet-700">
          {type === 'patent_spec' ? 'Prior art' : 'Doc'}
        </span>
      )}
      {title}
    </span>
  )
}

export default function EvidenceTable({ rows, rowCount = rows.length, shownCount = rows.length }: EvidenceTableProps) {
  if (rows.length === 0) {
    return (
      <p className="text-[11px] text-gray-500" data-testid="evidence-table-empty">
        No evidence rows to show.
      </p>
    )
  }

  return (
    <div data-testid="evidence-table">
      <div className="flex items-center gap-2 mb-1.5">
        <b className="text-xs text-iflow-900">Evidence — closest document pairs</b>
        <span className="flex-1" />
        <span className="text-[11px] text-gray-500">
          Showing {shownCount} of {rowCount} · all within your access
        </span>
      </div>
      <table className="w-full text-[11px] border-collapse" role="table" aria-label="Closest document-pair evidence">
        <thead>
          <tr>
            <th className="text-left text-[9.5px] font-bold uppercase tracking-wide text-gray-500 pb-1 border-b border-gray-200">Project A</th>
            <th className="text-left text-[9.5px] font-bold uppercase tracking-wide text-gray-500 pb-1 border-b border-gray-200">Project B</th>
            <th className="text-left text-[9.5px] font-bold uppercase tracking-wide text-gray-500 pb-1 border-b border-gray-200">How close these two documents are</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isExcluded = row.excluded_reason !== null
            return (
              <tr
                key={`${row.document_a_id}-${row.document_b_id}-${i}`}
                className={isExcluded ? 'opacity-60' : ''}
                data-testid={isExcluded ? 'evidence-row-excluded' : 'evidence-row'}
              >
                <td className="py-1.5 pr-2 border-b border-gray-100 align-top">
                  <DocCell title={row.document_a_title} type={row.document_a_type} />
                </td>
                <td className="py-1.5 pr-2 border-b border-gray-100 align-top">
                  <DocCell title={row.document_b_title} type={row.document_b_type} />
                </td>
                <td className="py-1.5 border-b border-gray-100 align-top font-semibold text-iflow-900">
                  {isExcluded ? (
                    <span className="text-gray-500 font-normal">{row.excluded_reason}</span>
                  ) : row.cosine_pct !== null ? (
                    overlapDocumentPairAnchor(row.cosine_pct)
                  ) : (
                    <span className="text-gray-400 font-normal">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
